import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Email Verification Page - handles the verification link click
export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        await axios.post(`${API_URL}/auth/verify-email?token=${token}`);
        setStatus('success');
        toast.success('Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        {status === 'verifying' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-neutral-900 mb-2">
              Verifying Your Email
            </h1>
            <p className="text-neutral-500">Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-neutral-900 mb-2">
              Email Verified!
            </h1>
            <p className="text-neutral-500 mb-6">
              Your email has been verified successfully. You can now log in to your account.
            </p>
            <Button 
              onClick={() => navigate('/login')} 
              className="btn-primary w-full"
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-neutral-900 mb-2">
              Verification Failed
            </h1>
            <p className="text-neutral-500 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/verify-email-pending')} 
                variant="outline"
                className="w-full"
              >
                Request New Link
              </Button>
              <Button 
                onClick={() => navigate('/login')} 
                className="btn-primary w-full"
              >
                Go to Login
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Pending Verification Page - shown after registration
export const VerifyEmailPendingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  // Get email from navigation state or query params
  useEffect(() => {
    const stateEmail = location.state?.email;
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (stateEmail) {
      setEmail(stateEmail);
    } else if (emailParam) {
      setEmail(emailParam);
    }
  }, [location.state]);

  const handleResend = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/resend-verification`, { email });
      setResent(true);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8"
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>

        <div className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-amber-600" />
          </div>
          
          <h1 className="text-2xl font-serif font-semibold text-neutral-900 mb-2">
            Check Your Email
          </h1>
          
          <p className="text-neutral-500 mb-2">
            We've sent a verification link to:
          </p>
          
          {email && (
            <p className="text-amber-600 font-semibold mb-4">
              {email}
            </p>
          )}
          
          <p className="text-neutral-500 mb-6">
            Please click the link in the email to verify your account and get started.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Verification email sent!</span>
            </div>
            <p className="text-sm text-green-600 mt-1">
              The email may take a few minutes to arrive. Be sure to check your spam folder.
            </p>
          </div>

          <div className="border-t border-neutral-200 pt-6 mt-6">
            <p className="text-sm text-neutral-500 mb-4">
              Didn't receive the email? Enter your email below to resend:
            </p>
            <div className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full h-12 px-4 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="resend-email-input"
              />
              
              <Button 
                onClick={handleResend}
                disabled={loading || resent}
                variant="outline"
                className="w-full h-12"
                data-testid="resend-verification-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : resent ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Email Sent! Check your inbox
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>
            </div>
          </div>

          <Button 
            onClick={() => navigate('/login')}
            className="btn-primary w-full h-12 mt-6"
          >
            Back to Login
          </Button>

          <p className="text-xs text-neutral-400 mt-6">
            The verification link expires in 24 hours.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
